// src/components/forms/order/OrderGeneralInfo.tsx
// Componente para información general del pedido

import React from 'react'
import StatusSelector from '../StatusSelector'
import type { OrderData } from '../../../types/orderFormTypes'
import { tipoLead, tipoCliente, marcas, tiendas } from '../../../assets/data/arrays'
import {
  filterStoreSelectOptions,
  isManagerMarketplaceRole,
} from '../../../constants/managerMarketplaceRole'
import { Select, Input } from '../../ui'
import { FormField } from '../../ui/Form'
import SectionTitle from '../../ui/SectionTitle'
import { InformationCircleIcon } from '@heroicons/react/24/outline'

interface OrderGeneralInfoProps {
  formData: OrderData
  mode: 'view' | 'edit' | 'create'
  errors: { [key: string]: string }
  recogeEnBodega: boolean
  userRoleId: number
  onInputChange: (section: keyof OrderData, field: string, value: any) => void
}

export const OrderGeneralInfo: React.FC<OrderGeneralInfoProps> = ({
  formData,
  mode,
  errors,
  recogeEnBodega,
  userRoleId,
  onInputChange
}) => {
  const user = JSON.parse(localStorage.getItem('user') || '{}')
  
  const getAgentName = () => {
    if (mode === 'create') {
      return `${user.first_name || ''} ${user.last_name || ''}`.trim().replace(/\b\w/g, l => l.toUpperCase())
    }
    if (formData.agente && typeof formData.agente === 'object' && 'first_name' in formData.agente && 'last_name' in formData.agente) {
      return `${(formData.agente as {first_name?: string, last_name?: string}).first_name || ''} ${(formData.agente as {first_name?: string, last_name?: string}).last_name || ''}`.trim().replace(/\b\w/g, l => l.toUpperCase())
    }
    return 'Sitios Web'
  }

  const getAgentId = () => {
    if (mode === 'create') {
      return user.id || ''
    }
    if (formData.agente && typeof formData.agente === 'object' && 'id' in formData.agente) {
      return (formData.agente as {id?: string | number}).id
    }
    return typeof formData.agente === 'number' ? formData.agente : ''
  }

  const marcasOrdenadas = [
    ...marcas.filter((m) => m.value === ''),
    ...marcas
      .filter((m) => m.value !== '')
      .sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }))
  ]

  const tiendasOrdenadas = filterStoreSelectOptions(
    userRoleId,
    [...tiendas].sort((a, b) =>
      a.label.localeCompare(b.label, 'es', { sensitivity: 'base' })
    )
  )

  const canChangeStoreInEdit = isManagerMarketplaceRole(userRoleId)
  const storeSelectDisabled =
    mode === 'view' || (mode === 'edit' && !canChangeStoreInEdit)

  return (
    <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
      <div className="flex justify-between items-center mb-4">
        <SectionTitle icon={InformationCircleIcon}>Información General</SectionTitle>
        <div className="flex items-center gap-2">
          <label className="text-sm font-semibold text-gray-700">Agente:</label>
          <span className="text-sm text-gray-900">{getAgentName()}</span>
          <input 
            type="hidden" 
            value={getAgentId()} 
            onChange={(e) => onInputChange('agente', '', e.target.value)}
          />
        </div>
      </div>
      {errors.agente && <span className="text-red-500 text-sm mb-2 block">{errors.agente}</span>}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatusSelector
          value={formData.status || ''}
          onChange={(value) => onInputChange('status', '', value)}
          mode={mode}
          userRoleId={userRoleId}
          recogeEnBodega={recogeEnBodega}
          currentStatus={formData.status}
          paymentMethod={formData.extraDetails?.payment_method}
          errors={errors}
          disabled={mode === 'view'}
        />

        {/* Tipo Lead */}
        <FormField
          label="Tipo Lead"
          required
          error={errors.tipo_lead}
        >
          <Select
            value={formData.extraDetails?.tipo_lead || ''}
            onChange={(e) => onInputChange('extraDetails', 'tipo_lead', e.target.value)}
            disabled={mode === 'view'}
            options={[
              { value: '', label: 'Seleccione' },
              ...tipoLead.map((tipo) => ({
                value: tipo.value,
                label: tipo.label
              }))
            ]}
            placeholder="Seleccione"
            error={!!errors.tipo_lead}
            errorMessage={errors.tipo_lead}
            mode={mode}
          />
        </FormField>

        {/* Tipo Cliente */}
        <FormField
          label="Tipo Cliente"
          required
          error={errors.tipo_cliente}
        >
          <Select
            value={formData.extraDetails?.tipo_cliente || ''}
            onChange={(e) => onInputChange('extraDetails', 'tipo_cliente', e.target.value)}
            disabled={mode === 'view'}
            options={[
              { value: '', label: 'Seleccione' },
              ...tipoCliente.map((tipo) => ({
                value: tipo.value,
                label: tipo.label
              }))
            ]}
            placeholder="Seleccione"
            error={!!errors.tipo_cliente}
            errorMessage={errors.tipo_cliente}
            mode={mode}
          />
        </FormField>

        {/* Marca */}
        <FormField
          label="Marca"
          required
          error={errors.marca}
        >
          <Select
            value={formData.extraDetails?.marca || ''}
            onChange={(e) => onInputChange('extraDetails', 'marca', e.target.value)}
            disabled={mode === 'view'}
            options={marcasOrdenadas.map((marca) => ({
              value: marca.value,
              label: marca.label
            }))}
            placeholder="Seleccione"
            error={!!errors.marca}
            errorMessage={errors.marca}
            mode={mode}
          />
        </FormField>

        {/* Tienda */}
        <FormField
          label="Tienda"
          required
          error={errors.store_name}
        >
          <Select
            value={formData.store_name}
            onChange={(e) => onInputChange('store_name', '', e.target.value)}
            disabled={storeSelectDisabled}
            options={[
              { value: '', label: 'Seleccione' },
              ...tiendasOrdenadas.map((tienda) => ({
                value: tienda.value,
                label: tienda.label
              }))
            ]}
            placeholder="Seleccione"
            error={!!errors.store_name}
            errorMessage={errors.store_name}
            mode={mode}
          />
        </FormField>

        {/* ID de Origen - Solo para HomeCenter y MercadoLibre en modo create */}
        {mode === 'create' && (formData.store_name === 'Homecenter' || formData.store_name === 'Mercado Libre') && (
          <FormField
            label="ID de Origen"
            error={errors.id_origen}
          >
            <Input
              type="text"
              value={formData.extraDetails?.id_origen || ''}
              onChange={(e) => onInputChange('extraDetails', 'id_origen', e.target.value)}
              placeholder="ID de origen del pedido"
              error={!!errors.id_origen}
              errorMessage={errors.id_origen}
              mode={mode}
            />
          </FormField>
        )}

        {/* Seguimiento - Solo para MercadoLibre en modo create */}
        {mode === 'create' && formData.store_name === 'Mercado Libre' && (
          <FormField
            label="Seguimiento"
            error={errors.seguimiento}
          >
            <Input
              type="text"
              value={formData.extraDetails?.seguimiento || ''}
              onChange={(e) => onInputChange('extraDetails', 'seguimiento', e.target.value)}
              placeholder="Seguimiento del pedido"
              error={!!errors.seguimiento}
              errorMessage={errors.seguimiento}
              mode={mode}
            />
          </FormField>
        )}
      </div>
    </div>
  )
}
